import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { TranslateConfigService } from '@core/translate-config.service';
import { ManualPage } from '@shared/models/enums/manualPage.enum';


@Injectable({ providedIn: 'root' })
export class ManualContentService {
  private readonly fallbackLang = 'en';
  private readonly imageBaseUrl = '/assets/manual/media';
  private readonly cache = new Map<string, Observable<SafeHtml | null>>();

  private readonly allowedEmbedHosts = ['loom.com'];

  /**
   * Manual articles are static assets, not API calls, so they use an HttpClient
   * built directly from HttpBackend to skip `ApiInterceptor`. That interceptor is
   * for backend calls — it attaches the auth token, drives the global loader, and
   * turns any non-2xx into an error snackbar. A missing .md (404) is expected here
   * and handled with a language fallback, so it must not trigger that error toast
   * (nor flash the loader / send an auth header for a static-file fetch).
   */
  private readonly httpBackend = inject(HttpBackend);
  private readonly http = new HttpClient(this.httpBackend);

  private readonly sanitizeConfig = {
    ALLOWED_TAGS: [
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'a', 'ul', 'ol', 'li', 'strong',
      'em', 'b', 'i', 'u', 'code', 'pre', 'blockquote', 'hr', 'br', 'img',
      'iframe', 'div', 'span', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'id', 'class', 'style', 'width', 'height',
      'target', 'rel', 'allow', 'allowfullscreen', 'frameborder', 'loading',
    ],
  };

  constructor(
    private sanitizer: DomSanitizer,
    private translateConfigService: TranslateConfigService
  ) {
    DOMPurify.addHook('uponSanitizeElement', (node, data) => {
      if (data.tagName === 'iframe') {
        const src = node instanceof Element ? node.getAttribute('src') ?? '' : '';
        if (!this.isAllowedEmbedSrc(src)) {
          node.parentNode?.removeChild(node);
        }
      }
    });
  }

  getManual(key: ManualPage): Observable<SafeHtml | null> {
    const lang = this.translateConfigService.currentLanguage || this.fallbackLang;
    const cacheKey = `${lang}/${key}`;
    if (!this.cache.has(cacheKey)) {
      const article$ = this.fetchMarkdown(lang, key).pipe(
        map(markdown => (markdown.trim() ? this.render(markdown) : null)),
        shareReplay(1)
      );
      this.cache.set(cacheKey, article$);
    }
    return this.cache.get(cacheKey)!;
  }

  private isAllowedEmbedSrc(src: string): boolean {
    try {
      const url = new URL(src, window.location.origin);
      if (url.protocol !== 'https:') {
        return false;
      }
      return this.allowedEmbedHosts.some(
        host => url.hostname === host || url.hostname.endsWith(`.${host}`)
      );
    } catch {
      return false;
    }
  }

  private fetchMarkdown(lang: string, key: string): Observable<string> {
    return this.loadFile(lang, key).pipe(
      switchMap(content => {
        if (content !== null) {
          return of(content);
        }
        return lang === this.fallbackLang
          ? of('')
          : this.loadFile(this.fallbackLang, key).pipe(map(fallback => fallback ?? ''));
      })
    );
  }

  private loadFile(lang: string, key: string): Observable<string | null> {
    return this.http
      .get(`/assets/manual/languages/${lang}/${key}.md`, { responseType: 'text' })
      .pipe(catchError(() => of(null)));
  }

  private render(markdown: string): SafeHtml {
    const resolved = markdown.replace(/\{\{img\}\}/g, this.imageBaseUrl);

    let html = marked.parse(resolved, { async: false }) as string;
    html = this.addHeadingIds(html);

    const clean = DOMPurify.sanitize(html, this.sanitizeConfig);
    return this.sanitizer.bypassSecurityTrustHtml(clean);
  }

  private addHeadingIds(html: string): string {
    return html.replace(/<h([1-6])>(.*?)<\/h\1>/g, (_match, level, inner) => {
      const slug = inner
        .replace(/<[^>]+>/g, '')
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-');
      return `<h${level} id="${slug}">${inner}</h${level}>`;
    });
  }
}
